import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, KeyRound } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/2r5b`;
const PLACEMENT_LABELS = {
  foran_hoveddor: "Foran hoveddør", ved_bagdor: "Ved bagdør",
  carport: "I carport", skur: "I skur", reception: "Reception",
  varemodtagelse: "Varemodtagelse", other: "Andet (skriv selv)",
};

export default function PublicFuldmagtPage() {
  const { token } = useParams();
  const [state, setState] = React.useState(null);
  const [step, setStep] = React.useState("loading"); // loading | choose | otp | done | cancelled | invalid
  const [placement, setPlacement] = React.useState("foran_hoveddor");
  const [freeText, setFreeText] = React.useState("");
  const [agree, setAgree] = React.useState(false);
  const [otp, setOtp] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/fuldmagt/${token}`);
        setState(data);
        if (!data.ok) setStep("invalid");
        else if (data.status === "active") setStep("done");
        else setStep("choose");
      } catch { setStep("invalid"); }
    })();
  }, [token]);

  const create = async () => {
    if (placement === "other" && freeText.trim().length < 2) return toast.error("Skriv venligst placering");
    if (!agree) return toast.error("Du skal acceptere betingelserne");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/fuldmagt/${token}/create`,
        { placement, placement_free_text: freeText, terms_accepted: true });
      if (data.needs_verification) setStep("otp");
      else setStep("done");
    } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/fuldmagt/${token}/verify`, { otp_code: otp });
      toast.success("Fuldmagt aktiveret");
      setStep("done");
    } catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Annullér fuldmagt?")) return;
    try { await axios.post(`${API}/fuldmagt/${token}/cancel`); toast.success("Annulleret"); setStep("cancelled"); }
    catch (e) { toast.error(e.response?.data?.detail || "Fejl"); }
  };

  if (step === "loading") return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" size={24}/></div>;
  if (step === "invalid") return (
    <div className="p-8 max-w-md mx-auto" data-testid="fuldmagt-invalid">
      <div className="rounded-lg border border-red-300 bg-red-50 p-6">
        <AlertCircle className="text-red-700 mb-2" size={32}/>
        <h2 className="font-bold mb-1">Linket er ikke gyldigt</h2>
        <p className="text-sm text-brand-text2">Fuldmagten er udløbet, annulleret eller pakken er allerede leveret.</p>
      </div>
    </div>
  );
  if (step === "done") return (
    <div className="p-8 max-w-md mx-auto" data-testid="fuldmagt-done">
      <div className="rounded-lg border border-green-300 bg-green-50 p-6">
        <CheckCircle2 className="text-green-700 mb-2" size={32}/>
        <h2 className="font-bold mb-1">Fuldmagt aktiveret</h2>
        <p className="text-sm">Chaufføren vil stille pakken på det aftalte sted.</p>
        <button onClick={cancel} className="mt-3 text-xs text-brand-text2 underline">Annullér fuldmagt</button>
      </div>
    </div>
  );
  if (step === "cancelled") return (
    <div className="p-8 max-w-md mx-auto"><div className="rounded-lg border border-gray-300 bg-gray-50 p-6">Fuldmagt annulleret.</div></div>
  );
  if (step === "otp") return (
    <div className="p-8 max-w-md mx-auto" data-testid="fuldmagt-otp-step">
      <h2 className="text-xl font-bold mb-2">Bekræft med SMS-kode</h2>
      <p className="text-sm text-brand-text2 mb-4">Vi har sendt en 6-cifret kode til dig på SMS.</p>
      <input type="text" maxLength={6} className="input-flat w-full text-2xl text-center font-mono" value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="fuldmagt-otp-input" placeholder="000000"/>
      <button onClick={verify} disabled={busy || otp.length < 4} className="mt-3 w-full px-4 py-2 bg-brand-red text-white rounded font-semibold disabled:opacity-50" data-testid="fuldmagt-otp-verify">
        {busy && <Loader2 className="inline animate-spin mr-1" size={14}/>}Bekræft
      </button>
    </div>
  );
  return (
    <div className="p-8 max-w-lg mx-auto" data-testid="fuldmagt-choose-step">
      <h1 className="text-2xl font-bold mb-1">Giv fuldmagt</h1>
      <p className="text-sm text-brand-text2 mb-4">EMA Kurer kan stille pakken på et aftalt sted, hvis du ikke er hjemme.</p>
      {state?.parcel && <div className="rounded bg-brand-bg2 p-3 mb-4 text-sm"><strong>Pakke:</strong> {state.parcel.shipment_number} · {state.parcel.delivery_address}, {state.parcel.delivery_postal_code} {state.parcel.delivery_city}</div>}
      <div className="space-y-2 mb-4">
        {Object.entries(PLACEMENT_LABELS).map(([k, lbl]) => (
          <label key={k} className="flex items-center gap-2 p-2 border border-brand rounded hover:bg-brand-bg2/30 cursor-pointer">
            <input type="radio" name="placement" value={k} checked={placement === k} onChange={() => setPlacement(k)} data-testid={`fuldmagt-placement-${k}`}/>
            <span className="text-sm">{lbl}</span>
          </label>
        ))}
        {placement === "other" && <input className="input-flat w-full" placeholder="Beskriv placering" value={freeText} onChange={(e) => setFreeText(e.target.value)} data-testid="fuldmagt-other-text"/>}
      </div>
      <label className="flex items-start gap-2 text-sm mb-4">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="fuldmagt-agree"/>
        <span>Jeg accepterer, at chaufføren stiller pakken på det aftalte sted, og at jeg overtager risikoen for pakken efter levering.</span>
      </label>
      <button onClick={create} disabled={busy} className="w-full px-4 py-3 bg-brand-red text-white rounded font-semibold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="fuldmagt-submit">
        {busy ? <Loader2 className="animate-spin" size={16}/> : <KeyRound size={16}/>}
        Giv fuldmagt
      </button>
    </div>
  );
}
